/**
 * MarketIntelRegulatoryPage — /omega/market-intel/regulatory
 *
 * Regulatory signals viewer for the Market Intelligence Brain (Phase 40 V-025).
 * Fetches /api/v1/market-intel/regulatory?category={category}&jurisdiction={jurisdiction}.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

const CATEGORIES = [
  'Financial',
  'HealthAndWellbeing',
  'TechnologyAndInfrastructure',
  'WorkAndProductivity',
];

const SEVERITIES: Record<string, string> = {
  Blocking: '#f87171',
  Material: '#fbbf24',
  Advisory: '#34d399',
};

interface RegulatorySignal {
  signal_id: string;
  category: string;
  jurisdiction: string;
  regulation_name: string;
  severity: string;
  urgency: string;
  summary: string;
  source_url: string;
  effective_date: string | null;
  days_until_enforcement: number | null;
  data_stale: boolean;
}

interface RegulatoryResponse {
  signals: RegulatorySignal[];
}

export function MarketIntelRegulatoryPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [category, setCategory] = useState('Financial');
  const [jurisdiction, setJurisdiction] = useState('');

  const qs = new URLSearchParams({ category });
  if (jurisdiction) qs.set('jurisdiction', jurisdiction);

  const { data, isLoading, error } = useQuery({
    queryKey: ['market-intel-regulatory', category, jurisdiction],
    queryFn: () => apiFetch<RegulatoryResponse>(`/market-intel/regulatory?${qs}`),
  });

  const signals = data?.signals ?? [];

  return (
    <div className="omega-page" style={{ padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Market Intel', to: '/omega/market-intel' },
          { label: 'Regulatory' },
        ]}
      />

      <h1 style={{ fontSize: '12pt', fontWeight: 700, margin: '12px 0 16px' }}>
        Regulatory Signals
      </h1>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <label style={{ fontSize: '9pt', marginRight: 8 }}>Category:</label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={{ fontSize: '9pt', padding: '4px 8px' }}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: '9pt', marginRight: 8 }}>Jurisdiction:</label>
          <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} style={{ fontSize: '9pt', padding: '4px 8px' }}>
            <option value="">All</option>
            {['EU', 'US', 'IL', 'UK', 'global'].map(j => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
      </div>

      {isLoading && <p style={{ fontSize: '9pt' }}>Loading...</p>}
      {error && <p style={{ fontSize: '9pt', color: '#f87171' }}>Error loading signals.</p>}
      {!isLoading && signals.length === 0 && (
        <p style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)' }}>No signals found.</p>
      )}

      {signals.map(s => (
        <div
          key={s.signal_id}
          style={{
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 4,
            padding: '10px 14px',
            marginBottom: 8,
            fontSize: '8.5pt',
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontWeight: 700 }}>{s.regulation_name}</span>
            <span style={{ color: SEVERITIES[s.severity] ?? 'var(--omega-ink)', fontWeight: 600 }}>{s.severity}</span>
            <span style={{ color: 'var(--omega-ink-muted)' }}>{s.jurisdiction}</span>
            {s.data_stale && <span style={{ color: '#f87171' }}>STALE</span>}
          </div>
          <div style={{ color: 'var(--omega-ink-muted)', marginBottom: 4 }}>{s.summary}</div>
          {s.days_until_enforcement != null && (
            <div style={{ fontSize: '8pt' }}>Enforcement in {s.days_until_enforcement} days</div>
          )}
        </div>
      ))}
    </div>
  );
}
